import * as React from 'react';
import eventNames from './eventNames';
import loadSdk from './loadSdk';
import { CommonPlayerApi } from 'components/VideoPlayer';

interface YouTubeProps {
    /**
     * An 11-character string representing a YouTube video ID..
     */
    video: string,

    /**
     * DOM ID for the player element.
     */
    id?: string,

    /**
     * CSS className for the player element.
     */
    className?: string,
    /**
     * Width of the player element.
     */
    width?: number | string,

    /**
     * Height of the player element.
     */
    height?: number | string,

    /**
     * Pause the video.
     */
    paused?: boolean, // eslint-disable-line react/no-unused-prop-types

    // Player parameters

    /**
     * Whether the video should start playing automatically.
     *
     * https://developers.google.com/youtube/player_parameters#autoplay
     */
    autoplay?: boolean,
    /**
     * Whether to show captions below the video.
     *
     * https://developers.google.com/youtube/player_parameters#cc_load_policy
     */
    showCaptions?: boolean,

    /**
     * Whether to show video controls.
     *
     * https://developers.google.com/youtube/player_parameters#controls
     */
    controls?: boolean,

    /**
     * Ignore keyboard controls.
     *
     * https://developers.google.com/youtube/player_parameters#disablekb
     */
    disableKeyboard?: boolean,

    /**
     * Whether to display the fullscreen button.
     *
     * https://developers.google.com/youtube/player_parameters#fs
     */
    allowFullscreen?: boolean,

    /**
     * The player's interface language. The parameter value is an ISO 639-1
     * two-letter language code or a fully specified locale.
     *
     * https://developers.google.com/youtube/player_parameters#hl
     */
    lang?: string,

    /**
     * Whether to show annotations on top of the video.
     *
     * https://developers.google.com/youtube/player_parameters#iv_load_policy
     */
    annotations?: boolean,

    /**
     * Time in seconds at which to start playing the video.
     *
     * https://developers.google.com/youtube/player_parameters#start
     */
    startSeconds?: number,

    /**
     * Time in seconds at which to start playing the video.
     *
     * https://developers.google.com/youtube/player_parameters#start
     */
    seek?: number,


    /**
     * Time in seconds at which to stop playing the video.
     *
     * https://developers.google.com/youtube/player_parameters#end
     */
    endSeconds?: number,

    /**
     * Remove most YouTube logos from the player.
     *
     * https://developers.google.com/youtube/player_parameters#modestbranding
     */
    modestBranding?: boolean,

    /**
     * Whether to play the video inline on iOS, instead of fullscreen.
     *
     * https://developers.google.com/youtube/player_parameters#playsinline
     */
    playsInline?: boolean,

    /**
     * Whether to show related videos after the video is over.
     *
     * https://developers.google.com/youtube/player_parameters#rel
     */
    showRelatedVideos?: boolean,

    /**
     * Whether to show video information (uploader, title, etc) before the video
     * starts.
     *
     * https://developers.google.com/youtube/player_parameters#showinfo
     */
    showInfo?: boolean,

    /**
     * The playback volume, **as a number between 0 and 1**.
     */
    volume?: number,

    /**
     * Whether the video's sound should be muted.
     */
    muted?: boolean,

    /**
     * The suggested playback quality.
     *
     * https://developers.google.com/youtube/iframe_api_reference#Playback_quality
     */
    suggestedQuality?: string,

    /**
     * Playback speed.
     *
     * https://developers.google.com/youtube/iframe_api_reference#setPlaybackRate
     */
    playbackRate?: number,


    // Events


    /**
     * Sent when the player triggers an error.
     */
    onError?: () => void,

    /**
     * Sent when the video is cued and ready to play.
     */
    onCued?: (event: any, api: CommonPlayerApi) => void,

    /**
     * Sent when the video is buffering.
     */
    onBuffering?: (event: any, api: CommonPlayerApi) => void,

    /**
     * Sent when playback has been started or resumed.
     */
    onPlaying?: (event: any, api: CommonPlayerApi) => void,

    /**
     * Sent when playback has been paused.
     */
    onPause?: (event: any, api: CommonPlayerApi) => void,

    /**
     * Sent when playback has stopped.
     */
    onSeeking?: (event: any, api: CommonPlayerApi) => void,

    /**
     * Sent when the is playing
     */
    onProgress?: (event: any, api: CommonPlayerApi) => void,

    /**
     * Sent when playback has been end.
     */
    onEnd?: (event: any, api: CommonPlayerApi) => void,

    /**
     * Sent when playback state has changed.
     */
    onStateChange?: (event: any, api: CommonPlayerApi) => void,

    /**
     * Get player instance
     */
    onGetPlayerInstance?: (ref: any) => void;
};


class YouTube extends React.Component<YouTubeProps> {

    private container: HTMLDivElement;
    private playerInstance: any;
    private playing: boolean = false;
    private lastTime: number = 0;
    private mytimer: NodeJS.Timeout = null;


    constructor(props: YouTubeProps) {
        super(props);

        this.onPlayerReady = this.onPlayerReady.bind(this);
        this.onPlayerStateChange = this.onPlayerStateChange.bind(this);
    }

    static defaultProps = {
        autoplay: false,
        showCaptions: false,
        controls: true,
        disableKeyboard: false,
        allowFullscreen: true,
        annotations: true,
        modestBranding: false,
        playsInline: false,
        showRelatedVideos: true,
        showInfo: true,
        onCued: () => { },
        onBuffering: () => { },
        onPlaying: () => { },
        onPause: () => { },
        onEnd: () => { },
        onProgess: () => { }
    };

    ///
    private buildApi() {
        const duration = this.playerInstance.getDuration();
        const time = this.playerInstance.getCurrentTime();
        const seeking = Math.abs(time - this.lastTime) > 1.05;
        if (!seeking) this.lastTime = time;

        const api: CommonPlayerApi = {
            duration,
            time,
            seeking,
            seek: (v: number) => this.playerInstance.seekTo(v),
        };
        return api;
    }

    ///
    private emulateProgress() {
        const progress = () => {
            const api = this.buildApi();
            this.props.onProgress(null, api);
            if (this.props.onSeeking && api.seeking) this.props.onSeeking(event, api);
        };

        progress();
        this.mytimer = setInterval(progress, 1000);
    };

    ///
    private stopProgress() {
        if (this.mytimer) {
            clearInterval(this.mytimer);
            this.mytimer = null;
        }
    }
    ///
    private onPlayerReady(event: any) {
        const {
            volume,
            muted,
            suggestedQuality,
            playbackRate,
        } = this.props;

        if (typeof volume !== 'undefined') {
            event.target.setVolume(volume * 100);
        }
        if (typeof muted !== 'undefined') {
            if (muted) {
                event.target.mute();
            } else {
                event.target.unMute();
            }
        }
        if (typeof suggestedQuality !== 'undefined') {
            event.target.setPlaybackQuality(suggestedQuality);
        }
        if (typeof playbackRate !== 'undefined') {
            event.target.setPlaybackRate(playbackRate);
        }

        if (this.props.onGetPlayerInstance) this.props.onGetPlayerInstance(event.target);
    }

    ///
    private onPlayerStateChange(event: any) {
        const {
            onCued,
            onBuffering,
            onPause,
            onPlaying,
            onEnd,
            onProgress
        } = this.props;

        const State = window["YT"].PlayerState;
        switch (event.data) {
            case State.UNSTARTED:
                onCued(event, this.buildApi());
                break;
            case State.BUFFERING:
                onBuffering(event, this.buildApi());
                break;

            case State.PAUSED:
                this.stopProgress();
                this.playing = false;
                onPause(event, this.buildApi());
                break;

            case State.PLAYING:
                if (!this.playing) {
                    this.stopProgress();
                    this.playing = true;
                    onPlaying(event, this.buildApi());
                    if (onProgress) this.emulateProgress();
                }
                break;

            case State.ENDED:
                this.stopProgress();
                this.playing = false;
                onEnd(event, this.buildApi());
                break;

            default:
            // Nothing
        }
    }

    ///
    private getPlayerParameters() {
        /* eslint-disable react/destructuring-assignment */
        return {
            autoplay: this.props.autoplay,
            cc_load_policy: this.props.showCaptions ? 1 : 0,
            controls: this.props.controls ? 1 : 0,
            disablekb: this.props.disableKeyboard ? 1 : 0,
            fs: this.props.allowFullscreen ? 1 : 0,
            hl: this.props.lang,
            iv_load_policy: this.props.annotations ? 1 : 3,
            start: this.props.startSeconds,
            end: this.props.endSeconds,
            modestbranding: this.props.modestBranding ? 1 : 0,
            playsinline: this.props.playsInline ? 1 : 0,
            rel: this.props.showRelatedVideos ? 1 : 0,
            showinfo: this.props.showInfo ? 1 : 0,
        };
        /* eslint-enable react/destructuring-assignment */
    }

    ///
    private getInitialOptions() {
        const ev = {
            onReady: this.onPlayerReady,
            onStateChange: this.onPlayerStateChange
        };

        eventNames.forEach((name) => {
            const handler = this.props[name];
            if (handler) {
                ev[name] = (event: any) => handler(event);
            }
        });

        return {
            playerId: this.props.id,
            videoId: this.props.video,
            width: this.props.width || "auto",
            height: this.props.height || "auto",
            playerVars: this.getPlayerParameters(),
            events: ev
        };
    }

    ///
    private updateProps(propNames: any) {

        var player = this.playerInstance;

        propNames.forEach((name: any) => {
            // eslint-disable-next-line react/destructuring-assignment
            const value = this.props[name];
            switch (name) {
                case "seek":
                    if (value) {
                        player.seekTo(value);
                    }
                    break;

                case 'muted':
                    if (value) {
                        player.mute();
                    } else {
                        player.unMute();
                    }
                    break;

                case 'suggestedQuality':
                    player.setPlaybackQuality(value);
                    break;

                case 'volume':
                    player.setVolume(value * 100);
                    break;

                case 'paused':
                    if (value && player.getPlayerState() !== 2) {
                        player.pauseVideo();
                    } else if (!value && player.getPlayerState() === 2) {
                        player.playVideo();
                    }
                    break;

                case 'id':
                case 'className':
                case 'width':
                case 'height':
                    player.getIframe()[name] = value; // eslint-disable-line no-param-reassign
                    break;

                case 'video':
                    if (!value) {
                        player.stopVideo();
                    } else {
                        const { startSeconds, endSeconds, autoplay } = this.props;
                        const opts = {
                            videoId: value,
                            startSeconds: startSeconds || 0,
                            endSeconds,
                        };
                        if (autoplay) {
                            player.loadVideoById(opts);
                        } else {
                            player.cueVideoById(opts);
                        }
                    }
                    break;

                default:
                // Nothing
            }
        });
    }

    ///
    private createPlayer() {
        var container = this.container;
        var options = this.getInitialOptions();
        loadSdk()
            .then((YT: any) => {
                try {
                    this.playerInstance= new YT.Player(container.id, options);
                }
                catch (error) {
                    console.log(error);
                }
            });
    }

    ///
    componentDidMount() {
        if (this.container != null)
            this.createPlayer();
    }

    ///
    componentDidUpdate(prevProps: YouTubeProps) {
        // eslint-disable-next-line react/destructuring-assignment
        const changes = Object.keys(this.props).filter((name) => name == "seek" || this.props[name] !== prevProps[name]);

        this.updateProps(changes);
    }

    ///
    componentWillUnmount() {
        if (this.playerInstance) {
            this.playerInstance.destroy();
        }
    }

    ///
    render() {
        const { id, className } = this.props;

        return (
            <div
                id={id}
                className={className}
                ref={(ref: HTMLDivElement) => { this.container = ref; }}
            />
        );
    }
}

export default YouTube;